/**
 * The last leg of an upload, handed to the service worker so it can finish
 * after the app is closed.
 *
 * An upload is three steps: createUpload hands back a presigned URL, the bytes
 * are PUT to R2, and finalizeUpload writes the message. The first needs the
 * page -- a photo is shrunk on a canvas, a clip measured in a <video> -- but
 * the last two need only the bytes, the ticket and the sender's ID token. So
 * once the ticket is in hand the page writes all of that down here as a leg
 * and asks for a background sync. On Android, Chrome then wakes the worker
 * when it is online, even with the app closed, and the worker finishes the
 * leg. Safari has no background sync; there a leg simply waits for the page,
 * which finishes it on the way back as before.
 *
 * Both may reach the same leg. The page holds a lease while it is sending,
 * renewed every few seconds, and the worker waits for that to lapse -- which
 * is what a closed or frozen page does -- before taking over. Each sends the
 * same bytes to the same key and finalizes the same upload, so even both at
 * once is harmless: the second finalize is told the upload is already closed,
 * and that counts as done.
 */
import type { FinalizeUploadRequest } from '../../shared/types';

export interface Leg {
  /** The queue's job this finishes; done means the job is done. */
  jobId: string;
  uploadId: string;
  url: string;
  headers: Record<string, string>;
  /** When the presigned URL stops working, in ms. */
  expiresAt: number;
  body: Blob;
  /**
   * A photo's small copy and where it goes, sent before the photo. Best
   * effort: a photo whose copy never lands is drawn whole.
   */
  small?: { url: string; headers: Record<string, string>; body: Blob };
  finalize: FinalizeUploadRequest;
  /** The sender's Firebase ID token, good for an hour, to finalize as them. */
  idToken: string;
  /** Whoever is sending it holds this until then, in ms. */
  leaseUntil: number;
}

/** The background sync the page asks for, and the worker answers. */
export const LEG_SYNC = 'givisnap-uploads';
/** How long a page's hold on a leg lasts without being renewed. */
export const LEASE_MS = 20_000;
/** How often a page renews it while it is still sending. */
export const RENEW_MS = 5_000;
/** How long the worker waits on a page that is still sending, before trying again later. */
const WAIT_MS = 90_000;
const POLL_MS = 3_000;

export interface LegStore {
  allLegs(): Promise<Leg[]>;
  legOf(jobId: string): Promise<Leg | undefined>;
  saveLeg(leg: Leg): Promise<void>;
  forgetLeg(jobId: string): Promise<void>;
  forgetJob(jobId: string): Promise<void>;
}

export interface WorkerEdges {
  store: LegStore;
  fetch: typeof fetch;
  now: () => number;
  sleep: (ms: number) => Promise<void>;
  /** Where the callable functions answer, e.g. https://us-central1-<project>.cloudfunctions.net */
  functions: string;
}

/** The worker's own hold on a leg: long enough for a clip on a slow line. */
const WORKER_LEASE_MS = 5 * 60_000;

/**
 * In the worker: finish every leg the page handed on. Throws if any could not
 * be finished and could be, so the browser asks again later; a leg only the
 * page can finish -- its URL run out, its token expired -- is left for it.
 */
export async function finishLegs(edges: WorkerEdges): Promise<void> {
  let unfinished = false;
  for (const first of await edges.store.allLegs()) {
    const leg = await waitOutThePage(first, edges);
    // Finished by the page while we waited.
    if (!leg) continue;
    // The ticket has run out; the page asks for a new one when it is back.
    if (leg.expiresAt <= edges.now()) continue;
    // Still the page's after all this time: try again on the next wake.
    if (leg.leaseUntil > edges.now()) {
      unfinished = true;
      continue;
    }

    await edges.store.saveLeg({ ...leg, leaseUntil: edges.now() + WORKER_LEASE_MS });
    const outcome = await send(leg, edges).catch(() => 'retry' as const);
    if (outcome === 'done') {
      await edges.store.forgetLeg(leg.jobId);
      await edges.store.forgetJob(leg.jobId);
      continue;
    }
    // Let the page have it straight back if it comes first.
    await edges.store.saveLeg({ ...leg, leaseUntil: 0 });
    if (outcome === 'retry') unfinished = true;
  }
  if (unfinished) throw new Error('uploads left to finish');
}

/** The leg once nobody is sending it, or undefined if it was finished meanwhile. */
async function waitOutThePage(leg: Leg, edges: WorkerEdges): Promise<Leg | undefined> {
  const giveUpAt = edges.now() + WAIT_MS;
  let current: Leg | undefined = leg;
  while (current && current.leaseUntil > edges.now() && edges.now() < giveUpAt) {
    await edges.sleep(POLL_MS);
    current = await edges.store.legOf(leg.jobId);
  }
  return current;
}

/** The bytes, then the finalize. 'page' when only the page can do it now. */
async function send(leg: Leg, edges: WorkerEdges): Promise<'done' | 'retry' | 'page'> {
  // The browser sets the length itself and refuses to be told it.
  const sendable = (headers: Record<string, string>) =>
    Object.fromEntries(Object.entries(headers).filter(([key]) => key.toLowerCase() !== 'content-length'));
  if (leg.small) {
    await edges
      .fetch(leg.small.url, { method: 'PUT', headers: sendable(leg.small.headers), body: leg.small.body })
      .catch(() => undefined);
  }
  const put = await edges.fetch(leg.url, { method: 'PUT', headers: sendable(leg.headers), body: leg.body });
  if (!put.ok) return put.status === 403 ? 'page' : 'retry';

  const finalized = await edges.fetch(`${edges.functions}/finalizeUpload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${leg.idToken}` },
    body: JSON.stringify({ data: leg.finalize }),
  });
  if (finalized.ok) return 'done';
  // The page finished it first.
  if (await alreadyClosed(finalized)) return 'done';
  // An expired token: the page finishes it as its sender, on the way back.
  return finalized.status === 401 ? 'page' : 'retry';
}

async function alreadyClosed(response: Response): Promise<boolean> {
  const body = (await response.json().catch(() => null)) as { error?: { status?: string } } | null;
  return body?.error?.status === 'FAILED_PRECONDITION';
}
